import { HttpClient } from './http-client';
import { 
  Division, 
  DivisionsListResponse, 
  CreateDivisionRequest, 
  UpdateDivisionRequest,
  DivisionConfigExport,
  DivisionConfigImportRequest,
  DivisionConfigImportResult,
  ApiError
} from '../types';

export class DivisionService {
  constructor(private httpClient: HttpClient) {}

  /**
   * Get all divisions with optional filtering
   */
  async getDivisions(params?: {
    active?: boolean;
    search?: string;
  }): Promise<DivisionsListResponse> {
    const searchParams = new URLSearchParams();
    
    if (params?.active !== undefined) {
      searchParams.append('active', params.active.toString());
    }
    
    if (params?.search) {
      searchParams.append('search', params.search);
    }

    const queryString = searchParams.toString();
    const url = `/api/v1/divisions${queryString ? `?${queryString}` : ''}`;
    
    return this.httpClient.get<DivisionsListResponse>(url);
  }

  /**
   * Get a specific division by ID
   */
  async getDivision(divisionId: string): Promise<Division> {
    return this.httpClient.get<Division>(`/api/v1/divisions/${divisionId}`);
  }

  /**
   * Create a new division
   */
  async createDivision(request: CreateDivisionRequest): Promise<Division> {
    return this.httpClient.post<Division>('/api/v1/divisions', request);
  }

  /**
   * Update an existing division
   */
  async updateDivision(divisionId: string, request: UpdateDivisionRequest): Promise<Division> {
    return this.httpClient.put<Division>(`/api/v1/divisions/${divisionId}`, request);
  }

  /**
   * Delete (deactivate) a division
   */
  async deleteDivision(divisionId: string): Promise<void> {
    return this.httpClient.delete<void>(`/api/v1/divisions/${divisionId}`);
  }

  /**
   * Export division configuration as a JSON blob (triggers file download in the browser).
   */
  async exportDivisionConfiguration(divisionId: string): Promise<Blob> {
    return this.httpClient.getBlob(`/api/v1/divisions/${divisionId}/configuration/export`);
  }

  /**
   * Import a previously-exported division configuration into a division.
   */
  async importDivisionConfiguration(
    divisionId: string,
    request: DivisionConfigImportRequest
  ): Promise<DivisionConfigImportResult> {
    return this.httpClient.post<DivisionConfigImportResult>(
      `/api/v1/divisions/${divisionId}/configuration/import`,
      request
    );
  }

  /**
   * Generate (or regenerate) the division's public self-registration link slug.
   * Pass a `slug` for a friendly custom link (e.g. "acme-division"); omit it for a random one.
   */
  async generateRegistrationLink(divisionId: string, slug?: string): Promise<Division> {
    return this.httpClient.post<Division>(`/api/v1/divisions/${divisionId}/registration-link`, { slug });
  }

  /**
   * Disable the division's public self-registration link.
   */
  async clearRegistrationLink(divisionId: string): Promise<Division> {
    return this.httpClient.delete<Division>(`/api/v1/divisions/${divisionId}/registration-link`);
  }
}
